import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Grid,
  Stack,
  Typography,
  Button,
  Divider,
  Chip,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import CardFields from 'components/shared/payment/CardFields';
import PromoChip from 'components/shared/PromoChip';
import couponApi from 'api/rest/coupon';
import checkoutApi from 'api/rest/checkout';
import useSubscription from 'hooks/payments/useSubscription';
import logoSrc from 'images/logo.svg';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

const PLAN_QUERY = gql`
  query PlanCheckout($id: ID!) {
    plan(id: $id) {
      id
      name
      description
      price_cop
      sessions
      is_subscribable
      session_types {
        id
        name
      }
    }
    subscriptionTerms {
      es
      en
    }
  }
`;

function SubscriptionCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['payments']);
  const { id: userId, full_name: fullName } = useSelector((state) => state.auth.user);

  const { status, errorMessage, startSubscription, fetchSavedCard } = useSubscription(userId);
  const [card, setCard] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  // Beneficio de organización: se auto-aplica por membresía (sin código). Si existe, ocultamos
  // el campo de cupón para no confundir ni apilar descuentos.
  const [orgQuote, setOrgQuote] = useState(null);
  // Consentimiento de suscripción recurrente (nos desliga): obligatorio para suscribirse.
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setValidatingCoupon(true);
    setCouponError('');
    try {
      const result = await couponApi.validateCoupon(code, id);
      if (result.valid) {
        setCoupon(result);
      } else {
        setCoupon(null);
        setCouponError(result.reason || t('subscription.checkout.couponInvalid', { defaultValue: 'Cupón inválido.' }));
      }
    } catch {
      setCouponError(t('subscription.checkout.couponInvalid', { defaultValue: 'Cupón inválido.' }));
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Cupón pre-aplicado desde el modal de planes (?coupon=CODE): se valida una vez al cargar.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const prefill = searchParams.get('coupon');
    if (!prefill || !id) return;
    setCouponInput(prefill.toUpperCase());
    (async () => {
      try {
        const result = await couponApi.validateCoupon(prefill.trim(), id);
        if (result.valid) setCoupon(result);
        else setCouponError(result.reason || t('subscription.checkout.couponInvalid', { defaultValue: 'Cupón inválido.' }));
      } catch {
        setCouponError(t('subscription.checkout.couponInvalid', { defaultValue: 'Cupón inválido.' }));
      }
    })();
  }, [id, searchParams, t]);

  // Beneficio de organización (auto por membresía): se resuelve al cargar el checkout.
  useEffect(() => {
    let active = true;
    if (id) {
      checkoutApi.getQuote(id)
        .then((q) => {
          if (active && q && Number(q.discount_percent) > 0 && q.source === 'organization') {
            setOrgQuote(q);
          }
        })
        .catch(() => {});
    }
    return () => { active = false; };
  }, [id]);

  // Tarjeta guardada (payment_source reusable): si existe, se ofrece pagar sin reescribir.
  useEffect(() => {
    let active = true;
    fetchSavedCard()
      .then((c) => { if (active && c?.payment_source_id) setSavedCard(c); })
      .catch(() => {});
    return () => { active = false; };
  }, [fetchSavedCard]);

  const { data, loading } = useQuery(PLAN_QUERY, { variables: { id }, fetchPolicy: 'network-only' });
  const plan = data?.plan;

  const cardHolder = (() => {
    const name = (fullName || '').trim();
    return name.length >= 5 ? name : 'Cliente OTRIX';
  })();

  const handleSubmit = () => {
    setAttempted(true);
    if (!card?.isValid) return;
    startSubscription({
      planId: id,
      couponId: coupon?.coupon_id,
      card: {
        number: card.number,
        exp_month: card.exp_month,
        exp_year: card.exp_year,
        cvc: card.cvc,
        card_holder: cardHolder,
      },
    });
  };

  const handleSavedCardPay = () => {
    startSubscription({ planId: id, paymentSourceId: savedCard.payment_source_id, couponId: coupon?.coupon_id });
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '60vh' }}>
        <CircularProgress sx={{ color: 'var(--otx-gold-text)' }} />
      </Stack>
    );
  }

  if (!plan || !plan.is_subscribable || !plan.price_cop) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ mt: 6 }}>
        <Typography color="text.secondary">{t('subscription.checkout.notAvailable')}</Typography>
        <Button onClick={() => navigate('/dashboard')} variant="outlined">{t('subscription.checkout.back')}</Button>
      </Stack>
    );
  }

  const priceLabel = `$${Number(plan.price_cop).toLocaleString('es-CO')}`;
  // Beneficio de organización activo (auto por membresía). Tiene precedencia sobre el cupón
  // manual, que queda oculto para los miembros de una organización.
  const orgBenefit = Boolean(orgQuote && Number(orgQuote.discount_percent) > 0);
  // Precio realmente cobrado: cupón > beneficio de organización > precio base. El botón
  // "Suscribirme" debe mostrar el mismo total que el resumen.
  let discountedPriceNum = null;
  if (coupon) {
    discountedPriceNum = Number(coupon.discounted_price);
  } else if (orgBenefit) {
    discountedPriceNum = Number(orgQuote.discounted_price);
  }
  const hasDiscount = discountedPriceNum != null;
  const effectivePriceLabel = hasDiscount ? `$${discountedPriceNum.toLocaleString('es-CO')}` : priceLabel;

  // ----- Pantalla de éxito -----
  if (status === 'success') {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ minHeight: '60vh', textAlign: 'center', px: 2 }}>
        <SEOPageTitle title={t('subscription.checkout.successTitle')} />
        <CheckCircleIcon sx={{ fontSize: 96, color: '#1B934B' }} />
        <Typography variant="h4" fontWeight={800}>{t('subscription.checkout.successTitle')}</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
          {t('subscription.checkout.successBody', { plan: plan.name })}
        </Typography>
        <Button
          onClick={() => navigate('/dashboard')}
          sx={{ ...premiumButtonSx, mt: 1, px: 4 }}>
          {t('subscription.checkout.continue')}
        </Button>
      </Stack>
    );
  }

  const processing = status === 'processing';

  // Sección de cupón manual (solo para quienes NO tienen beneficio de organización).
  const couponSection = coupon ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <PromoChip discountValue={coupon.discount_value} discountType={coupon.discount_type} />
      <Button variant="text" size="small" onClick={() => { setCoupon(null); setCouponInput(''); }} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('subscription.checkout.removeCoupon', { defaultValue: 'Quitar' })}
      </Button>
    </Stack>
  ) : (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField
        size="small"
        value={couponInput}
        onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
        placeholder={t('subscription.checkout.couponPlaceholder', { defaultValue: 'Código de cupón' })}
        error={Boolean(couponError)}
        helperText={couponError || ' '}
        sx={{ flex: 1 }}
      />
      <Button onClick={applyCoupon} disabled={validatingCoupon || !couponInput.trim()} variant="outlined" color="inherit" sx={{ mt: 0.25, color: 'text.primary', borderColor: 'hairline.strong', textTransform: 'none', fontWeight: 700 }}>
        {validatingCoupon ? <CircularProgress size={16} color="inherit" /> : t('subscription.checkout.applyCoupon', { defaultValue: 'Aplicar' })}
      </Button>
    </Stack>
  );

  // Parámetros de suscripción/cancelación (editables por admin), en el idioma del usuario.
  const subTerms = data?.subscriptionTerms;
  const termsText = (i18n.language?.startsWith('en') ? subTerms?.en : subTerms?.es) || subTerms?.es || '';

  // Bloque de consentimiento: obligatorio para habilitar el botón de suscribirse.
  const consentBlock = (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', ml: 0 }}
      control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} sx={{ pt: 0, color: 'gold.main', '&.Mui-checked': { color: 'gold.main' } }} />}
      label={(
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('subscription.checkout.consent', { defaultValue: 'Acepto pagar una suscripción recurrente y entiendo que puedo cancelarla según los' })}{' '}
          <Box component="span" onClick={(e) => { e.preventDefault(); setTermsOpen(true); }} sx={{ color: 'var(--otx-gold-text)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
            {t('subscription.checkout.consentParamsLink', { defaultValue: 'parámetros de suscripción y cancelación' })}
          </Box>.
        </Typography>
      )}
    />
  );

  return (
    <Box sx={{ width: '100%', py: { xs: 2, md: 3 }, px: { xs: 0.5, md: 1 } }}>
      <SEOPageTitle title={`${t('subscription.checkout.submit')} · ${plan.name}`} />

      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIcon />}
        sx={{
          mb: 2.5,
          px: 1.5,
          color: 'text.secondary',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          '&:hover': { color: 'text.primary', backgroundColor: 'action.hover' },
        }}>
        {t('subscription.checkout.back')}
      </Button>

      <Grid
        container
        spacing={0}
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'hairline.strong',
          boxShadow: 'var(--otx-card-shadow-hover)',
        }}>
        {/* IZQUIERDA — resumen del plan */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            backgroundColor: 'surface.app',
            p: { xs: 3, md: 4 },
            color: 'text.primary',
            borderRight: { md: '1px solid' },
            borderColor: { md: 'hairline.main' },
          }}>
          <Box component="img" src={logoSrc} alt="OTRIX" sx={{ height: 34, mb: 4, filter: 'var(--otx-logo-filter)' }} />

          <Chip
            label={t('subscription.checkout.title')}
            size="small"
            sx={{ backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', fontWeight: 800, letterSpacing: 0.4, mb: 2 }}
          />
          <Typography variant="h5" fontWeight={800}>{plan.name}</Typography>

          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 2 }}>
            {hasDiscount && (
              <Typography variant="h6" sx={{ color: 'text.secondary', textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>{priceLabel}</Typography>
            )}
            <Typography variant="h3" fontWeight={900} sx={{ color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
              {effectivePriceLabel}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('subscription.checkout.perMonthLong', { currency: 'COP' })}
            </Typography>
          </Stack>

          {/* Descuento: beneficio de organización (auto) tiene precedencia y oculta el cupón. */}
          <Box sx={{ mt: 2 }}>
            {orgBenefit ? (
              <Chip
                label={t('subscription.checkout.orgBenefit', {
                  defaultValue: 'Beneficio {{org}} · -{{pct}}%',
                  org: orgQuote.organization_name || '',
                  pct: orgQuote.discount_percent
                })}
                size="small"
                sx={{ backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', fontWeight: 800, letterSpacing: 0.3 }}
              />
            ) : couponSection}
          </Box>

          <Divider sx={{ my: 3, borderColor: 'hairline.strong' }} />

          {plan.sessions > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <FitnessCenterIcon sx={{ fontSize: 18, color: 'var(--otx-gold-text)' }} />
              <Typography variant="body2"><b>{plan.sessions}</b> {t('subscription.checkout.sessionsIncluded')}</Typography>
            </Stack>
          )}
          {plan.session_types?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
              {plan.session_types.map((st) => (
                <Chip key={st.id} label={st.name} size="small" sx={{ backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', fontWeight: 600, fontSize: '0.7rem' }} />
              ))}
            </Stack>
          )}
          {plan.description && (
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{plan.description}</Typography>
          )}

          <Box sx={{ mt: 3, p: 1.5, borderRadius: 2, backgroundColor: 'surface.overlay', border: '1px solid', borderColor: 'hairline.main' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('subscription.checkout.autoRenew')}
            </Typography>
          </Box>
        </Grid>

        {/* DERECHA — tarjeta */}
        <Grid item xs={12} md={7} sx={{ backgroundColor: 'surface.content', p: { xs: 3, md: 4 } }}>
          {processing ? (
            <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ minHeight: 320 }}>
              <CircularProgress size={64} thickness={3} sx={{ color: 'var(--otx-gold-text)' }} />
              <Typography color="text.primary">{t('subscription.processing.message')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('subscription.checkout.processingSub')}</Typography>
            </Stack>
          ) : (
            <Stack spacing={2.5}>
              <Typography variant="h6" fontWeight={800} color="text.primary">{t('subscription.checkout.paymentData')}</Typography>

              {savedCard && !useNewCard ? (
                <>
                  {/* Tarjeta guardada: pagar sin reescribir */}
                  <Stack direction="row" spacing={1.5} alignItems="center"
                    sx={{ p: 2, borderRadius: 2, backgroundColor: 'surface.overlay', border: '1px solid', borderColor: 'hairline.strong' }}>
                    <CreditCardIcon sx={{ color: 'var(--otx-gold-text)' }} />
                    <Box>
                      <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 700 }}>
                        {savedCard.card_brand || 'CARD'} •••• {savedCard.card_last4}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {t('subscription.checkout.savedCard', { defaultValue: 'Tarjeta guardada' })}
                      </Typography>
                    </Box>
                  </Stack>

                  {status === 'failed' && (
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(178,22,22,0.12)', border: '1px solid', borderColor: 'rgba(178,22,22,0.3)' }}>
                      <ErrorOutlineIcon sx={{ color: '#ff6b6b', fontSize: 20, mt: 0.2 }} />
                      <Typography variant="body2" sx={{ color: '#ffb3b3' }}>{errorMessage || t('subscription.failed.message')}</Typography>
                    </Stack>
                  )}

                  {consentBlock}
                  <Button fullWidth onClick={handleSavedCardPay} disabled={!agreed} sx={premiumButtonSx}>
                    {t('subscription.checkout.subscribeWithPrice', { price: effectivePriceLabel })}
                  </Button>
                  <Button fullWidth variant="text" onClick={() => setUseNewCard(true)}
                    sx={{ color: 'var(--otx-gold-text)', fontWeight: 700, textTransform: 'none' }}>
                    {t('subscription.checkout.useAnotherCard', { defaultValue: 'Usar otra tarjeta' })}
                  </Button>
                </>
              ) : (
                <>
                  <CardFields onChange={setCard} />

                  {status === 'failed' && (
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(178,22,22,0.12)', border: '1px solid', borderColor: 'rgba(178,22,22,0.3)' }}>
                      <ErrorOutlineIcon sx={{ color: '#ff6b6b', fontSize: 20, mt: 0.2 }} />
                      <Typography variant="body2" sx={{ color: '#ffb3b3' }}>{errorMessage || t('subscription.failed.message')}</Typography>
                    </Stack>
                  )}

                  {consentBlock}
                  <Button fullWidth onClick={handleSubmit} disabled={!agreed || (attempted && !card?.isValid)} sx={premiumButtonSx}>
                    {t('subscription.checkout.subscribeWithPrice', { price: effectivePriceLabel })}
                  </Button>

                  {savedCard && (
                    <Button fullWidth variant="text" onClick={() => setUseNewCard(false)}
                      sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'none' }}>
                      {t('subscription.checkout.useSavedCard', { defaultValue: 'Usar tarjeta guardada' })}
                    </Button>
                  )}
                </>
              )}

              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ color: 'text.secondary' }}>
                <LockOutlinedIcon sx={{ fontSize: 15 }} />
                <Typography variant="caption">{t('subscription.checkout.secureNote')}</Typography>
              </Stack>
            </Stack>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pr: 5 }}>
          {t('subscription.checkout.paramsTitle', { defaultValue: 'Parámetros de suscripción y cancelación' })}
          <IconButton onClick={() => setTermsOpen(false)} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>
            {termsText || t('subscription.checkout.paramsEmpty', { defaultValue: 'Aún no se han definido los parámetros. Contáctanos por soporte para más información.' })}
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default SubscriptionCheckout;
